import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Pencil } from 'lucide-react';
import { updateAdvanceSchema, type UpdateAdvanceInput, type EmployeeAdvance, type ApiError } from '@pms/shared';
import { payrollService } from '@/services/payroll.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogOverlay, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';

function getErrorMessage(e: unknown): string {
  const ax = e as AxiosError<ApiError>;
  return ax.response?.data?.error?.message ?? 'Something went wrong';
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface Props {
  advance: EmployeeAdvance;
  open: boolean;
  onClose: () => void;
}

export function EditAdvanceModal({ advance, open, onClose }: Props) {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm<UpdateAdvanceInput>({
    resolver: zodResolver(updateAdvanceSchema),
  });

  useEffect(() => {
    if (advance) {
      reset({
        amount:          parseFloat(advance.amount),
        advance_date:    advance.advance_date,
        deduction_year:  advance.deduction_year ?? undefined,
        deduction_month: advance.deduction_month ?? undefined,
        reason:          advance.reason ?? undefined,
      });
    }
  }, [advance, reset]);

  const deductionMonth = watch('deduction_month');
  const deductionYear  = watch('deduction_year');
  const deductionLabel = (() => {
    const m = Number(deductionMonth);
    const y = Number(deductionYear);
    if (m >= 1 && m <= 12 && y >= 2020) return `Will deduct from ${MONTHS[m - 1]} ${y} salary`;
    return '';
  })();

  const update = useMutation({
    mutationFn: (data: UpdateAdvanceInput) => payrollService.updateAdvance(advance.id, data),
    onSuccess: () => {
      toast.success('Advance updated');
      void queryClient.invalidateQueries({ queryKey: ['payroll', 'advances'] });
      onClose();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogOverlay />
      <DialogContent className="max-w-md">
        <DialogHeader className="mb-4">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Pencil className="h-4 w-4 text-teal-600" />
            Edit Advance
          </DialogTitle>
        </DialogHeader>

        <div className="mb-3 rounded-md bg-muted px-3 py-2 text-sm font-medium">
          {advance.first_name} {advance.last_name}
          {advance.employee_code && <span className="ml-1 text-muted-foreground">({advance.employee_code})</span>}
        </div>

        <form onSubmit={handleSubmit((d) => update.mutate(d))} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="ea_amount">Amount (₹)</Label>
              <Input id="ea_amount" type="number" min="1" {...register('amount')} />
              {errors.amount && <p className="text-xs text-red-500">{errors.amount.message}</p>}
            </div>
            <div className="space-y-1">
              <Label htmlFor="ea_date">Advance Date</Label>
              <Controller
                control={control}
                name="advance_date"
                render={({ field }) => (
                  <DatePicker id="ea_date" value={field.value ?? ''} onChange={(v) => field.onChange(v)} modal={false} />
                )}
              />
              {errors.advance_date && <p className="text-xs text-red-500">{errors.advance_date.message}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="ea_ded_month">Deduction Month</Label>
              <Input id="ea_ded_month" type="number" min="1" max="12" placeholder="1–12" {...register('deduction_month')} />
              {errors.deduction_month && <p className="text-xs text-red-500">{errors.deduction_month.message}</p>}
            </div>
            <div className="space-y-1">
              <Label htmlFor="ea_ded_year">Deduction Year</Label>
              <Input id="ea_ded_year" type="number" min="2020" max="2100" {...register('deduction_year')} />
              {errors.deduction_year && <p className="text-xs text-red-500">{errors.deduction_year.message}</p>}
            </div>
          </div>
          {deductionLabel && <p className="text-xs text-muted-foreground -mt-2">{deductionLabel}</p>}

          <div className="space-y-1">
            <Label htmlFor="ea_reason">Reason</Label>
            <Textarea id="ea_reason" rows={2} {...register('reason')} />
          </div>

          <div className="flex justify-end gap-3 pt-1">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={isSubmitting || update.isPending}>
              {update.isPending ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
